import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Drawer, DrawerContent, DrawerTrigger, DrawerClose } from '@/components/ui/drawer';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const QUICK_SUGGESTIONS = [
  "Making good progress",
  "Feeling focused",
  "Taking it slow today",
  "Need a break soon",
  "Crushing it",
  "Just getting started",
];

interface StatusUpdateProps {
  status: string | null;
  onUpdate: (content: string) => Promise<void>;
  variant?: 'floating' | 'inline';
  isHighlighted?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /** For first-time History tour: e.g. "history-note-button" */
  dataTourTarget?: string;
}

export function StatusUpdate({ status, onUpdate, variant = 'floating', isHighlighted = false, onOpenChange, dataTourTarget }: StatusUpdateProps) {
  const [content, setContent] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mql.matches);
    const handler = () => setPrefersReducedMotion(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
    // Always clear content when opening for a fresh entry
    if (open) {
      setContent('');
    }
  };

  const handleSave = async () => {
    if (!content.trim()) return;
    
    setIsSaving(true);
    try {
      await onUpdate(content);
      toast({
        title: "Saved!",
        description: "Your notes have been logged.",
      });
      setContent('');
      handleOpenChange(false);
    } catch (error) {
      toast({
        title: "Error saving",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setContent('');
    handleOpenChange(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setContent(suggestion);
  };

  const hasStatus = !!status;

  const cancelButton = (
    <Button
      variant="ghost"
      size="lg"
      onClick={handleCancel}
      disabled={isSaving}
      className="flex-1 h-12 rounded-xl text-base font-medium"
    >
      Cancel
    </Button>
  );

  const saveButton = (
    <Button
      size="lg"
      onClick={handleSave}
      disabled={isSaving || !content.trim()}
      className="flex-1 h-12 rounded-xl text-base font-medium shadow-lg shadow-primary/25"
    >
      {isSaving ? 'Saving...' : 'Save'}
    </Button>
  );

  // Shared body: header, suggestions, textarea (no DrawerClose)
  const statusContentBody = (
    <div className="space-y-6">
      <div className="text-center space-y-1">
        <h2 className="text-xl font-semibold tracking-tight">How's your day going?</h2>
        <p className="text-sm text-muted-foreground">Add a quick note or pick a mood</p>
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        {QUICK_SUGGESTIONS.map((suggestion) => (
          <button
            key={suggestion}
            onClick={() => handleSuggestionClick(suggestion)}
            className={cn(
              "px-4 py-2.5 text-sm font-medium rounded-full",
              "transition-all duration-200",
              "active:scale-95",
              content === suggestion
                ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25"
                : "bg-secondary/80 text-secondary-foreground hover:bg-secondary"
            )}
          >
            {suggestion}
          </button>
        ))}
      </div>
      <div className="px-1">
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Or write something..."
          aria-label="Daily note or mood"
          className={cn(
            "min-h-[140px] resize-none text-base leading-relaxed",
            "bg-secondary/50 border-0 rounded-2xl",
            "placeholder:text-muted-foreground/60",
            "focus-visible:ring-2 focus-visible:ring-primary/50"
          )}
        />
      </div>
    </div>
  );

  // Inline variant: actions include DrawerClose (only ever rendered inside Drawer)
  const statusContentInline = (
    <>
      {statusContentBody}
      <div className="flex items-center justify-center gap-3 pt-2">
        <DrawerClose asChild>{cancelButton}</DrawerClose>
        {saveButton}
      </div>
    </>
  );

  // Floating variant: actions have no DrawerClose (safe in Popover)
  const statusContentFloating = (
    <>
      {statusContentBody}
      <div className="flex items-center justify-center gap-3 pt-2">
        {cancelButton}
        {saveButton}
      </div>
    </>
  );

  // Inline variant - uses Drawer (bottom sheet) for Apple-style slide up
  if (variant === 'inline') {
    return (
      <Drawer open={isOpen} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>
          <motion.button
            whileTap={{ scale: 0.95 }}
            className="relative z-10 flex items-center justify-center rounded-2xl"
            style={{ width: 56, height: 56 }}
            {...(dataTourTarget ? { 'data-tour-target': dataTourTarget } : {})}
          >
            <MessageSquare 
              className={cn(
                "h-7 w-7 transition-all duration-300",
                isHighlighted 
                  ? "stroke-white scale-110" 
                  : "stroke-white/60 hover:stroke-white/90"
              )}
              strokeWidth={2}
            />
            {hasStatus && !isHighlighted && (
              <span
                className={cn(
                  'absolute top-2 right-2 w-2.5 h-2.5 bg-green-500 rounded-full',
                  !prefersReducedMotion && 'notification-dot-pulse'
                )}
              />
            )}
          </motion.button>
        </DrawerTrigger>

        <DrawerContent>
          {statusContentInline}
        </DrawerContent>
      </Drawer>
    );
  }

  // Floating variant (default) - for desktop
  return (
    <TooltipProvider>
      <div className="fixed bottom-6 right-6 z-50">
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={cn(
                    "w-12 h-12 rounded-full shadow-lg",
                    "flex items-center justify-center",
                    "bg-primary text-primary-foreground",
                    "transition-all duration-200",
                    "hover:shadow-xl",
                    "relative"
                  )}
                >
                  <MessageSquare className="h-5 w-5" />
                  {hasStatus && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-background" />
                  )}
                </motion.button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-[200px]">
              {hasStatus ? (
                <p className="text-xs truncate">{status}</p>
              ) : (
                <p className="text-xs">Log your daily notes</p>
              )}
            </TooltipContent>
          </Tooltip>

          <PopoverContent 
            side="top" 
            align="end" 
            className="w-80 p-5"
            sideOffset={12}
          >
            {statusContentFloating}
          </PopoverContent>
        </Popover>
      </div>
    </TooltipProvider>
  );
}
